import { useState } from 'react';
import { Modal, Input, Button, Upload, Alert, Spin, App } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import { useExtractTopic } from '@/hooks/useExtractTopic';
import type { ExtractedDraft } from '@/types/extractedTopic';

interface Props {
  open: boolean;
  onClose: () => void;
  onExtracted: (draft: ExtractedDraft) => void;
}

export function ImportFromTextModal({ open, onClose, onExtracted }: Props) {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const extractMut = useExtractTopic();

  async function run() {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await extractMut.mutateAsync({ source: 'text', text });
      if (res.status === 'failed' || !res.draft) {
        message.error(res.message ?? res.error ?? 'Extraction failed');
      } else {
        onExtracted(res.draft);
        message.success(`Extracted (confidence ${Math.round((res.confidence ?? 0) * 100)}%)`);
        setText('');
        onClose();
      }
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title="Import from text / JSON" onCancel={onClose} footer={null} destroyOnHidden width={640}>
      {busy ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin size="large" /></div>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Paste flyer text or JSON; AI normalizes it into title, content, and structured fields. Review before saving."
          />
          <Upload.Dragger
            accept=".json,.txt"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file: RcFile) => { file.text().then(setText); return false; }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag a .json / .txt file (or paste below)</p>
          </Upload.Dragger>
          <Input.TextArea
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste flyer JSON or text…"
            style={{ marginTop: 12, fontFamily: 'monospace' }}
          />
          <Button type="primary" block disabled={!text.trim()} onClick={run} style={{ marginTop: 12 }}>Extract</Button>
        </>
      )}
    </Modal>
  );
}
